import _startCase from "lodash/startCase";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";

import {
  CustomerCard,
  QuestionStatusIcon,
  ReactTable,
  TableDatePicker,
  TableDropdownSelector,
  TableQuestionStatusButton,
  useCan,
  usePermanentLocations,
  useSelectedLocation
} from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { MediaPreview, SnoozeButtonIcon } from "modules/LeadsDashboard/components";
import {
  useCustomerOkUserOptions,
  useLeaseCompanyOptions,
  useQuestionResultStatusOptions,
  useSnoozeStatusOptions,
  useSnoozeTypeOptions
} from "modules/LeadsDashboard/hooks";
import { useLeadsSnoozedFilters, useSelectedTableFilters, useSnoozedLeadsTableFilterHandlers } from "modules/LeadsDashboard/Leads/SnoozedLeads/hooks";
import { getFormattedDate, getLocationName } from "modules/LeadsDashboard/util";
import { PATHS } from "router/paths";

import type { AccessorColumnDef } from "@tanstack/react-table";
import type { Snooze } from "models";
import type { QuestionResultData } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/types";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";
import type { Dispatch, SetStateAction } from "react";
import type { ItemWithHistory } from "utils/itemsWithHistory";
import type { PaginatedItems } from "utils/paginatedItems";

type SnoozedLeadsTableProps = {
  setModalData: Dispatch<SetStateAction<AddInterventionPayload | QuestionResultData | null>>;
  snoozedLeads?: PaginatedItems<ItemWithHistory<Snooze>>;
  isFetching: boolean;
};

export const SnoozedLeadsTable = ({ setModalData, isFetching, snoozedLeads }: SnoozedLeadsTableProps) => {
  const { t } = useTranslation();

  const canCreateSnooze = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Snooze);
  const { selectedLocation } = useSelectedLocation();
  const { locations } = usePermanentLocations();
  const { snoozeTypeOptions } = useSnoozeTypeOptions();
  const { leaseCompanyOptions } = useLeaseCompanyOptions();
  const { snoozeStatusOptions } = useSnoozeStatusOptions();
  const { customerOkUserOptions } = useCustomerOkUserOptions();
  const { questionResultStatusOptions } = useQuestionResultStatusOptions();
  const {
    appointment_date_from,
    appointment_date_to,
    snooze_date_from,
    snooze_date_to,
    updated_on_from,
    updated_on_to,
    page: currentPage,
    page_size: pageSize
  } = useLeadsSnoozedFilters();
  const {
    selectedQuestions,
    areAllDateFiltersReset,
    selectedLeaseCompanyOption,
    selectedSnoozeStatusOption,
    selectedCustomerOkUserOption,
    interventionQuestionOptions,
    selectedTaskOption,
    selectedQuestionStatuses
  } = useSelectedTableFilters({ customerOkUserOptions, snoozeTypeOptions, snoozeStatusOptions });
  const {
    handleOnTaskSelect,
    handleOnLeaseSelect,
    handlePaginationSelect,
    handleOnQuestionSelect,
    handleOnSnoozeStatusSelect,
    handleOnCustomerOkUserSelect,
    handleSnoozedDateRangeChange,
    handleQuestionResultStatusSelect,
    handleAppointmentDateRangeChange,
    handleUpdatedOnDateRangeChange,
    handlePageSizeChange
  } = useSnoozedLeadsTableFilterHandlers({ snoozeTypeOptions, leaseCompanyOptions, snoozeStatusOptions });

  const columns = useMemo<AccessorColumnDef<ItemWithHistory<Snooze>, number>[]>(
    () => [
      {
        accessorFn: row => row.id,
        id: "appointment_date",
        header: () => (
          <div className="appointment-table-wrapper default-cursor th-vertical-alignment">
            <span className="no-wrap-text table-header-text">{_startCase(t("v8_appointment_date", "Appointment Date"))} </span>
            <TableDatePicker
              clearable
              dateStart={appointment_date_from}
              dateEnd={appointment_date_to}
              onChange={handleAppointmentDateRangeChange}
              inputClassName="appointment-date-snoozed-input"
            />
          </div>
        ),
        size: 120,
        cell: ({
          row: {
            original: { appointment }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{getFormattedDate(appointment?.time_car_app)}</span>
      },

      {
        accessorFn: row => row.id,
        id: "wo",
        header: () => <span className="no-wrap-text table-header-text th-vertical-alignment">WO # {snoozedLeads?.nb_items ?? 0}</span>,
        size: 50,
        cell: ({
          row: {
            original: { appointment }
          }
        }) => {
          if (appointment?.wo_nr === "0") return "";

          return (
            <p className="ellipsis ">
              <Link to={`${PATHS.APPOINTMENTS}/${appointment?.id}`}>
                <Icon className="arrow up right from square" />
                {appointment?.wo_nr}
              </Link>
            </p>
          );
        }
      },

      {
        accessorFn: row => row.id,
        id: "reg_number",
        header: () => (
          <div className="appointment-table-wrapper th-vertical-alignment">
            <TableDropdownSelector
              options={leaseCompanyOptions}
              multiple={false}
              withSearch={false}
              selected={[selectedLeaseCompanyOption]}
              trigger={<span className="ellipsis ellipsis-wo-nr table-header-text"> {t("v8_reg_number", "Reg #")}</span>}
              handleOnSelect={handleOnLeaseSelect}
            />
          </div>
        ),
        size: 50,
        cell: ({
          row: {
            original: { appointment }
          }
        }) => {
          if (!appointment?.reg_number) return "";

          return (
            <p className="ellipsis ellipsis-wo-nr">
              <Link to={`${PATHS.CAR_DETAIL}/${appointment.car_id}`}>
                <Icon className="arrow up right from square" />
                {appointment.reg_number}
                {appointment.is_lease_company && <Icon name="building" />}
              </Link>
            </p>
          );
        }
      },

      {
        accessorFn: row => row.id,
        id: "location_name",
        header: () => <span className="no-wrap-text table-header-text th-vertical-alignment">{t("v8_location", "Location")}</span>,
        size: 50,
        cell: ({
          row: {
            original: { dealer_location_id }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{getLocationName(dealer_location_id, locations)}</span>
      },

      {
        accessorFn: row => row.id,
        id: "snooze_date",
        header: () => (
          <div className="appointment-table-wrapper default-cursor th-vertical-alignment" onClick={e => e.stopPropagation()}>
            <span className="no-wrap-text table-header-text">{_startCase(t("v8_snooze_date", "Snooze Date"))} </span>
            <TableDatePicker
              clearable
              dateStart={snooze_date_from}
              dateEnd={snooze_date_to}
              onChange={handleSnoozedDateRangeChange}
              inputClassName="snooze-date-snoozed-input"
            />
          </div>
        ),
        size: 100,
        cell: ({
          row: {
            original: { snooze_date }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{getFormattedDate(snooze_date?.toString())}</span>
      },

      {
        accessorFn: row => row.id,
        id: "snooze_status",
        header: () => (
          <div className="appointment-table-wrapper th-vertical-alignment">
            <TableDropdownSelector
              trigger={<span className="ellipsis ellipsis-wo-nr table-header-text no-wrap-text"> {_startCase(t("v8_snooze_status", "Snooze Status"))}</span>}
              options={snoozeStatusOptions}
              selected={[selectedSnoozeStatusOption]}
              handleOnSelect={handleOnSnoozeStatusSelect}
            />
          </div>
        ),
        size: 100,
        cell: ({
          row: {
            original: { snooze_status_id }
          }
        }) => {
          const status = snoozeStatusOptions.find(option => option.key === snooze_status_id);
          return <span className="ellipsis ellipsis-wo-nr">{status?.text || ""}</span>;
        }
      },

      {
        accessorFn: row => row.id,
        id: "task",
        header: () => (
          <div className="appointment-table-wrapper th-vertical-alignment">
            <TableDropdownSelector
              trigger={<span className="ellipsis ellipsis-wo-nr table-header-text no-wrap-text"> {t("v8_task", "Task")}</span>}
              options={snoozeTypeOptions}
              selected={[selectedTaskOption]}
              handleOnSelect={handleOnTaskSelect}
            />
          </div>
        ),
        size: 80,
        cell: ({
          row: {
            original: { snooze_type_id }
          }
        }) => {
          const status = snoozeTypeOptions.find(option => option.key === snooze_type_id);
          return <span className="ellipsis ellipsis-wo-nr">{status?.text || ""}</span>;
        }
      },

      {
        accessorFn: row => row.id,
        id: "last_updated_by",
        header: () => <span className="no-wrap-text table-header-text no-wrap-text th-vertical-alignment">{_startCase(t("v8_last_updated_by", "Last Updated By"))}</span>,
        size: 80,
        cell: ({
          row: {
            original: { user }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{user ? `${user.first_name} ${user.last_name}`.trim() : ""}</span>
      },

      {
        accessorFn: row => row.id,
        id: "updated_on",
        header: () => (
          <div className="appointment-table-wrapper default-cursor th-vertical-alignment">
            <span className="no-wrap-text table-header-text">{_startCase(t("v8_updated_on", "Updated On"))} </span>
            <TableDatePicker
              clearable
              dateStart={updated_on_from}
              dateEnd={updated_on_to}
              onChange={handleUpdatedOnDateRangeChange}
              inputClassName="updated-on-snoozed-input"
            />
          </div>
        ),
        size: 100,
        cell: ({
          row: {
            original: { updated_on }
          }
        }) => <span className="ellipsis ellipsis-wo-nr">{getFormattedDate(updated_on)}</span>
      },

      {
        accessorFn: row => row.id,
        id: "customer_ok_user",
        header: () => (
          <div className="appointment-table-wrapper th-vertical-alignment">
            <TableDropdownSelector
              trigger={<span className="table-header-text no-wrap-text"> {t("v8_customer_ok_user", "Customer OK User")}</span>}
              selected={[selectedCustomerOkUserOption]}
              options={customerOkUserOptions}
              handleOnSelect={handleOnCustomerOkUserSelect}
            />
          </div>
        ),

        size: 50,
        cell: ({
          row: {
            original: { appointment }
          }
        }) => (
          <span className="ellipsis ellipsis-wo-nr">
            {appointment?.customer_ok_user ? `${appointment.customer_ok_user.first_name} ${appointment.customer_ok_user.last_name}`.trim() : ""}
          </span>
        )
      },

      {
        accessorFn: row => row.id,
        id: "intervention_question_result",
        header: () => (
          <div className="appointment-table-wrapper th-vertical-alignment">
            <TableDropdownSelector
              multiple={true}
              trigger={<span className="table-header-text no-wrap-text"> {t("v8_intervention_question_result", "Intervention/Question Result")}</span>}
              withSearch={true}
              selected={selectedQuestions}
              options={interventionQuestionOptions}
              handleOnSelect={handleOnQuestionSelect}
            />
          </div>
        ),
        size: 80,
        cell: ({
          row: {
            original: { question_result, intervention }
          }
        }) => <span>{question_result?.title || intervention?.title || ""}</span>
      },

      {
        accessorFn: row => row.id,
        id: "note",
        header: () => <span className="table-header-text no-wrap-text th-vertical-alignment"> {t("v8_note", "Note")} </span>,
        size: 100,
        cell: ({
          row: {
            original: { question_result, intervention, note }
          }
        }) => {
          const content = question_result?.mechanic_notes || intervention?.mechanic_notes || note || "";
          return (
            <Popup
              hoverable
              content={content}
              trigger={
                <p className="ellipsis ellipsis-model" style={{ width: "60px" }}>
                  {content}
                </p>
              }
            />
          );
        }
      },

      {
        accessorFn: row => row.id,
        id: "status",
        header: () => (
          <div className="table-action-buttons-container">
            <TableDropdownSelector
              multiple={true}
              selected={selectedQuestionStatuses}
              options={questionResultStatusOptions}
              handleOnSelect={handleQuestionResultStatusSelect}
              trigger={<TableQuestionStatusButton selectedQuestionStatuses={selectedQuestionStatuses} />}
              scrolling={false}
              showFilterIcon={false}
            />
            <div className="table-icon-placeholder-div" />
            <div className="table-icon-placeholder-div" />
          </div>
        ),
        size: 30,
        cell: ({
          row: {
            original: {
              appointment,
              question_result,
              question_result_id,
              intervention_id,
              snooze_type_id,
              snooze_status_id,
              snooze_department_id,
              snooze_date,
              note,
              appointment_id,
              history,
              car_id,
              intervention,
              car,
              user
            }
          }
        }) => {
          const snoozeModalData: QuestionResultData = {
            title: question_result?.title || intervention?.title || "",
            description: question_result?.raw || intervention?.description || "",
            solution: question_result?.solution || "",
            id: question_result_id || intervention_id || 0,
            images: question_result?.images || [],
            appointment_id: appointment_id,
            snoozed: question_result?.snoozed || intervention?.snoozed,
            snooze_history: history,
            snooze_type_id: snooze_type_id,
            note,
            car_id,
            car,
            user,
            intervention,
            appointment,
            ...(question_result && { question_result }),
            ...(question_result_id && { question_result_id }),
            ...(snooze_status_id && { snooze_status_id }),
            ...(snooze_department_id && { snooze_department_id }),
            ...(snooze_date && { snooze_date }),
            ...(intervention_id && { intervention_id: intervention_id })
          };

          return (
            <div className="table-action-buttons-container">
              <MediaPreview images={question_result?.images} videos={question_result?.videos} location={selectedLocation} />
              <QuestionStatusIcon status={question_result?.status ?? intervention?.question_result_status ?? null} />
              {appointment?.wo_nr !== "0" ? (
                <CustomerCard
                  contractor={appointment?.customer_contract}
                  driver={appointment?.customer_driver}
                  owner={appointment?.customer_owner}
                  driverOptedOutFromImporting={appointment?.driver_opted_out_from_importing}
                  ownerOptedOutFromImporting={appointment?.owner_opted_out_from_importing}
                  contractorOptedOutFromImporting={appointment?.contractor_opted_out_from_importing}
                />
              ) : (
                <div className="table-icon-placeholder-div" />
              )}
              <SnoozeButtonIcon disabled={!canCreateSnooze} isSnoozed={true} handleClick={() => setModalData(snoozeModalData)} />
            </div>
          );
        }
      }
    ],
    [
      t,
      locations,
      setModalData,
      snoozedLeads?.nb_items,
      appointment_date_from,
      appointment_date_to,
      snooze_date_from,
      snooze_date_to,
      updated_on_from,
      updated_on_to,
      leaseCompanyOptions,
      snoozeTypeOptions,
      snoozeStatusOptions,
      customerOkUserOptions,
      questionResultStatusOptions,
      interventionQuestionOptions,
      selectedQuestions,
      selectedLeaseCompanyOption,
      selectedSnoozeStatusOption,
      selectedCustomerOkUserOption,
      selectedTaskOption,
      selectedQuestionStatuses,
      handleOnTaskSelect,
      handleOnLeaseSelect,
      handleOnQuestionSelect,
      handleOnSnoozeStatusSelect,
      handleOnCustomerOkUserSelect,
      handleSnoozedDateRangeChange,
      handleQuestionResultStatusSelect,
      handleAppointmentDateRangeChange,
      handleUpdatedOnDateRangeChange
    ]
  );

  return (
    <div className="table-container">
      <ReactTable
        isFetching={isFetching}
        columns={columns}
        data={snoozedLeads?.items}
        pageCount={snoozedLeads?.nb_pages}
        pageSize={pageSize}
        totalItems={snoozedLeads?.nb_items}
        pagination={snoozedLeads && snoozedLeads.nb_pages > 1 ? { pageIndex: currentPage - 1, pageSize } : undefined}
        onPaginationChange={handlePaginationSelect}
        overrideTableBodyMessage={
          areAllDateFiltersReset ? `${t("v8_please_select_at_least_one_date_range_to_filter_on", "Please select at least one date range to filter on")}.` : null
        }
        onChangePageSize={handlePageSizeChange}
      />
    </div>
  );
};
